import { COMPANION_NAME } from '@/lib/companion-config';
import { NAV_KEY, navLabel, type NavKey } from '@/i18n/keys/nav';

/**
 * The impersonation menu registry — the single source of truth pairing the
 * sidebar MENU_KEY_MAP keys with human labels (#624 review).
 *
 * Lockstep contract: the sidebar filters (Sidebar.tsx / TRFerSidebar.tsx
 * MENU_KEY_MAP) fail OPEN for unmapped hrefs but fail CLOSED for mapped keys
 * missing from allowed_menus. Every key a MENU_KEY_MAP maps MUST have a label
 * here, or the item silently disappears from every impersonated session with
 * no checkbox to grant it. Pinned by tests in Sidebar.test / TRFerSidebar.test.
 */
export const PARENT_MENU_LABELS: Record<string, string> = {
  // The KEY stays `dashboard` — it is persisted in live `allowed_menus`
  // grants. Only the LABEL follows the page: the home page reads "Today" in
  // every shell (ratified DECISIONS 2026-08-26, design grammar §4.4), and
  // this string is what an admin reads on the grant checkbox and in the
  // impersonation banner, so it is one of the surfaces §4.4 covers.
  dashboard: 'Today',
  chat: `Chat / ${COMPANION_NAME}`,
  tasks: 'Tasks',
  calendar: 'Calendar',
  // The KEY stays `medical` and must: it is the string persisted in
  // `allowed_menus` and the value `MENU_KEY_MAP['/medical']` produces, so
  // renaming it would silently revoke the item from every existing
  // impersonation grant. Only the LABEL follows the page (`35601a57`:
  // MedicalPage → HealthPage), which is what the admin actually reads on the
  // checkbox and in the impersonation banner.
  medical: 'Health',
  // Covers the whole parent money surface, grants tab included
  // (/bills?tab=grants) — grants are not a separate menu entry.
  // The KEY stays `bills` — it is persisted in live allowed_menus grants
  // and renaming it would silently revoke the item from every one. Only
  // the label follows the page (2026-09-21).
  bills: 'Grants',
  // Label follows the sidebar (2026-09-18: Support & Resources → Support;
  // 2026-10-23: Support → Resources (via "Resources & contacts" for an
  // hour), TRF Announcements → Announcements).
  circle: 'Resources',
  community: 'Announcements',
  settings: 'Settings',
};

export const TRFER_MENU_LABELS: Record<string, string> = {
  // Key persisted, label follows the page — see PARENT_MENU_LABELS above.
  dashboard: 'Today',
  // Label follows the sidebar (2026-09-13: Referrals → Referral codes); the
  // key is the stable identity and never changes.
  referrals: 'Referral codes',
  // 2026-09-18: Threads · Live → Unreads, Conversations → Threads, Support &
  // Resources → Support, Spend & Expense → Spend (DECISIONS "Subtraction in
  // the chrome"). Labels follow the sidebar; the keys are persisted grants.
  threads: 'Unreads',
  // `families` grants the whole Families page since 2026-09-14 — the roster,
  // the Needs you view (what the `queue` key granted) and the Reports view
  // (what `reports` granted). `queue` and `reports` are retired the way
  // `pipeline` and `offers` were: nothing maps to them, so a stored grant
  // carrying either is harmless, and a key here with no row in MENU_KEY_MAP
  // would render a checkbox that governs nothing.
  families: 'Families',
  // `schedule` (#1084) is retired since 2026-09-19 the way `calendar` was:
  // the Schedule page is removed, its href redirects to Families, and no row
  // in MENU_KEY_MAP names the key — so a label here would render a checkbox
  // that governs nothing.
  // 2026-10-23: Support → Resources, TRF Announcements → Announcements,
  // Polls → Check-ins & polls. `conversations` is retired the way `schedule`
  // was: the Threads page is removed and its href redirects to Today, so a
  // label here would render a checkbox that governs nothing.
  resources: 'Resources',
  community: 'Announcements',
  reimbursements: 'Reimbursements (demo)',
  spend: 'Card charges (demo)',
  polls: 'Check-ins & polls',
  // `ledger` is retired since 2026-09-14 (#1979): the Ledger is the Ledger tab
  // of Settings, so `settings` grants it — the `handoff-config` reasoning.
  // 2026-09-14: the Grant totals page split. Awards keeps the `grant-totals`
  // key (keys never rename). Present here because a key mapped in
  // TRFerSidebar's MENU_KEY_MAP with no label FAILS CLOSED. `balances` retired
  // 2026-09-19 when Awards absorbed Balances.
  'grant-totals': 'Awards',
  // `users` grants the whole Users page since 2026-09-14 (#1980) — the roster
  // with its Pending signup filter (what `signups` granted); the Assignments
  // view `families-manage` granted moved to Families (wave w1024). Both keys are retired
  // the way `pipeline` and `offers` were. Label follows the sidebar.
  users: 'Users',
  applications: 'Applications',
  // `eligibility-appeals` (Pre-screen appeals, #1013) was retired on
  // 2026-10-26 with the appeals process (#3307): the page, its route and its
  // API are gone, so nothing maps to the key any more.
  // `pipeline` and `offers` were retired on 2026-09-13: both destinations
  // fold into /trfer/applications, which `applications` governs. Grants that
  // still carry either key are harmless — nothing maps to them any more.
  grants: 'Requests',
  forms: 'Forms',
  // `copy` (Family wording) is deliberately absent since 2026-08-29: it is the
  // Forms surface's second view, not a destination of its own, so `forms`
  // grants both. A key here with no row in MENU_KEY_MAP would render a
  // checkbox that governs nothing.
  // `handoff-config` is retired since 2026-09-14: Handoff config is the
  // Handoff tab of Settings, so `settings` grants it.
  settings: 'Settings',
};

/** Merged key→label lookup (shared keys use role-neutral labels). */
export const MENU_LABELS: Record<string, string> = {
  ...TRFER_MENU_LABELS,
  ...PARENT_MENU_LABELS,
  dashboard: 'Today',
  calendar: 'Calendar',
  settings: 'Settings',
  community: 'Community',
};

export interface MenuEntry {
  key: string;
  label: string;
}

/** The grantable menu list for an impersonation target, from its role set. */
export function menusForRoles(roles: string[]): MenuEntry[] {
  const out = new Map<string, string>();
  if (roles.includes('parent') || roles.includes('admin')) {
    for (const [key, label] of Object.entries(PARENT_MENU_LABELS)) out.set(key, label);
  }
  if (roles.includes('trfer') || roles.includes('admin')) {
    for (const [key, label] of Object.entries(TRFER_MENU_LABELS)) {
      out.set(key, out.has(key) ? MENU_LABELS[key] ?? label : label);
    }
  }
  if (out.size === 0) {
    for (const [key, label] of Object.entries(MENU_LABELS)) out.set(key, label);
  }
  return Array.from(out, ([key, label]) => ({ key, label }));
}

/**
 * The nav key each menu key's label is read from (#2109). Every label above
 * is a nav row's label, so the banner speaks the reader's language by
 * resolving the row's key rather than the English string. `chat` is the
 * BottomNav slot plus the companion's name (a proper noun, never a catalog
 * value). Pinned against `MENU_LABELS` in English by
 * `impersonationMenus.i18n.test.ts`.
 */
export const MENU_NAV_KEY: Record<string, NavKey> = {
  dashboard: NAV_KEY.today,
  tasks: NAV_KEY.tasks,
  calendar: NAV_KEY.calendar,
  medical: NAV_KEY.health,
  bills: NAV_KEY.grants,
  circle: NAV_KEY.support,
  // The merged map's role-neutral word for the announcements feed is the
  // nav group's label, "Community", not either shell's row label.
  community: NAV_KEY.groupCommunity,
  settings: NAV_KEY.settings,
  referrals: NAV_KEY.referralCodes,
  threads: NAV_KEY.unreads,
  families: NAV_KEY.families,
  resources: NAV_KEY.resources,
  reimbursements: NAV_KEY.reimbursements,
  spend: NAV_KEY.cardCharges,
  polls: NAV_KEY.polls,
  'grant-totals': NAV_KEY.awards,
  users: NAV_KEY.users,
  applications: NAV_KEY.applications,
  grants: NAV_KEY.grantRequests,
  forms: NAV_KEY.forms,
};

/**
 * Banner-facing label for an allowed_menus key, in the active language;
 * falls back to the English registry label, then to the raw key.
 */
export function menuLabel(key: string): string {
  if (key === 'chat') return `${navLabel(NAV_KEY.tabChat)} / ${COMPANION_NAME}`;
  const navKey = MENU_NAV_KEY[key];
  return navKey ? navLabel(navKey) : MENU_LABELS[key] ?? key;
}
